<template>
  <div v-if="showStudentSelection" class="category-div student-selection">
    <cs-form-dialog
      id="dlg-student-selection"
      v-model="studentSelectionDialogDisplayed"
      body-id="dlg-body-student-selection"
      heading="Select Students"
      :primary-action="{
        label: 'Save',
        eventName: 'primary-click',
        disabled: isLoading || !formControlEnabled || hasNoSelectedStudent
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click',
        disabled: isLoading
      }"
      :secondary-action2="{
        label: 'Update Student List',
        eventName: 'secondary2-click',
        disabled: isLoading || !formControlEnabled || isVendorServiceEnded
      }"
      @primary-click="warningUpdateStudentSelectionDialogDisplayed = true"
      @secondary1-click="onCancelStudentSelection"
      @secondary2-click="refreshStudentSelection(false)"
      @update:model-value="warningCancelStudentSelectionDialogDisplayed = true"
    >
      <template #cs-form-dialog-content>
        <div v-if="isVendorServiceEnded">
          <div style="height: 60px"></div>
        </div>
        <v-checkbox
          v-model="filterStudentShowSelectedOnly"
          label="Show selected students only"
          hide-details="true"
          color="primary"
          class="student-selection-checkbox mb-2"
          :disabled="isLoading"
        ></v-checkbox>
        <v-text-field
          v-model="filterStudentText"
          variant="solo"
          density="compact"
          clearable
          clear-icon="mdi-close"
          class="filter-student-text-field"
          placeholder="Filter student list"
          :disabled="isLoading"
        />
        <v-data-table
          v-model="selectedStudents"
          :headers="[
            { title: 'Student ID', key: 'id', width: '20%' },
            { title: 'Student Name', key: 'name', width: '50%' },
            { title: 'Groups', key: 'group', width: '30%' }
          ]"
          show-select
          return-object
          hover
          :items="filteredStudentOptions"
          :items-per-page="-1"
          :sort-by="[{ key: 'id', order: 'asc' }]"
          :search="filterStudentText"
          :loading="isLoading ? 'primary' : false"
          :disabled="isLoading"
        >
          <template #[`header.data-table-select`]="{ allSelected, selectAll, someSelected }">
            <v-checkbox-btn
              :indeterminate="someSelected && !allSelected"
              :model-value="allSelected"
              color="primary"
              class="student-selection-checkbox"
              @update:model-value="selectAll(!allSelected)"
            ></v-checkbox-btn>
          </template>
          <template #[`item.data-table-select`]="{ internalItem, isSelected, toggleSelect }">
            <v-checkbox-btn
              :model-value="isSelected(internalItem)"
              color="primary"
              class="student-selection-checkbox"
              @update:model-value="toggleSelect(internalItem)"
            ></v-checkbox-btn>
          </template>
        </v-data-table>
        <div style="text-align: center">
          Last List Updated At: {{ formatedStudentDataTimestamp }}
        </div>
      </template>
    </cs-form-dialog>

    <div v-if="!isStudentCountSynced">
      <v-alert color="#ff7f32" icon-size="x-small" variant="outlined" icon="mdi-alert">
        <b>Notice:</b> Student count differs from the Excursion record. This may result in
        inconsistent ratio data between systems. Please review your selection before proceeding.
      </v-alert>
    </div>
    <v-row align="center" justify="space-between">
      <v-col class="category-section d-flex align-center">
        <v-icon
          id="student-selection--toggle--icon"
          class="icon"
          :icon="!studentSelectionContentDisplayed ? 'mdi-chevron-right' : 'mdi-chevron-down'"
          @click.stop="toggle()"
        >
        </v-icon>
        <div id="student-selection--toggle--label" class="name" @click.stop="toggle()">
          Students
        </div>
        <div class="quantity ml-3">{{ totalSelectedStudents }} In Total</div>
      </v-col>

      <v-col class="student-selection--category-section">
        <div class="d-flex justify-end align-center flex-1-1">
          <div style="width: 231px">
            <v-checkbox
              id="student-selection--chk-group-other"
              v-model="otherGroupEnabled"
              label="Co-curricular and Sport Groups"
              density="compact"
              color="primary"
              hide-details="true"
              :disabled="isLoading || !formControlEnabled"
            ></v-checkbox>
          </div>
          <stb-icon-tooltip
            location="top"
            icon-color="rgb(119, 119, 119)"
            icon-id="fa:fas fa-info-circle"
            clickable
            max-width="450px"
          >
            <div class="type-tooltip-content">
              <span>Is your excursion for a co-curricular or sports related event? </span>
              <span>Please check the Co-curricular and Sport Groups box for relevant fields</span>
            </div>
          </stb-icon-tooltip>
          <cs-button
            id="student-selection--btn-select"
            :disabled="disabledSelectStudentButton"
            class="mt-0"
            :label="`${formControlEnabled ? 'Select' : 'View'} Students`"
            @click="openStudentSelection"
          ></cs-button>
        </div>
      </v-col>
    </v-row>
    <div :hidden="!studentSelectionContentDisplayed" class="sub-category-section">
      <div :hidden="otherGroupEnabled">
        <!-- SCHOOL -->
        <v-col cols="12" class="sub-label">
          <v-row align="center" justify="space-between">
            <div class="pl-1">School</div>
          </v-row>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" class="input-container px-0">
          <v-autocomplete
            id="student-selection--group-school"
            ref="selectGroupSchool"
            v-model="selectedGroupSchoolDisplayed"
            placeholder="Select one or more schools"
            :items="[{ id: 'all', name: 'Select All' }, ...groupSchoolOptions]"
            item-title="name"
            item-value="id"
            multiple
            color="primary"
            clearable
            chips
            closable-chips
            hide-details
            :hide-selected="autocompleteHideSelected"
            variant="solo"
            density="comfortable"
            clear-icon="mdi-close"
            class="selection-autocomplete"
            :loading="isLoading"
            loading-text="Loading..."
            :disabled="isLoading || !formControlEnabled"
            @update:model-value="
              (values) => handleGroupSelectionChange(values, $refs.selectGroupSchool, 'school')
            "
          >
            <template #item="data">
              <v-list-item
                v-bind="data.props"
                :id="`student-selection--group-school-item-${data.item.id}`"
              />
            </template>
          </v-autocomplete>
        </v-col>
        <!-- YEAR -->
        <v-col cols="12" class="sub-label">
          <v-row align="center" justify="space-between">
            <div class="pl-1">Year</div>
          </v-row>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" class="input-container px-0">
          <v-autocomplete
            id="student-selection--group-year"
            ref="selectGroupYear"
            v-model="selectedGroupYearsDisplayed"
            placeholder="Select one or more years"
            :items="[{ id: 'all', name: 'Select All' }, ...groupYearOptions]"
            item-title="name"
            item-value="id"
            multiple
            color="primary"
            clearable
            chips
            closable-chips
            hide-details
            :hide-selected="autocompleteHideSelected"
            variant="solo"
            density="comfortable"
            clear-icon="mdi-close"
            class="selection-autocomplete"
            :loading="isLoading"
            loading-text="Loading..."
            :disabled="isLoading || !formControlEnabled || selectedGroupSchools.length === 0"
            @update:model-value="
              (values) => handleGroupSelectionChange(values, $refs.selectGroupYear, 'year')
            "
          >
            <template #item="data">
              <v-list-item
                v-bind="data.props"
                ::id="`student-selection--group-year-item-${data.item.id}`"
              />
            </template>
          </v-autocomplete>
        </v-col>
        <!-- SUBJECT -->
        <div :hidden="!filterFromSelectedSubject">
          <v-col cols="12" class="sub-label">
            <v-row align-center justify-space-between>
              <div class="pl-1">Subject</div>
            </v-row>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" class="input-container px-0">
            <v-autocomplete
              id="student-selection--group-subject"
              ref="selectGroupSubject"
              v-model="selectedGroupSubjectDisplayed"
              placeholder="Select one or more subjects"
              :items="[{ id: 'all', name: 'Select All' }, ...groupSubjectOptions]"
              item-title="name"
              item-value="id"
              multiple
              color="primary"
              clearable
              chips
              closable-chips
              hide-details
              :hide-selected="autocompleteHideSelected"
              variant="solo"
              density="comfortable"
              clear-icon="mdi-close"
              class="selection-autocomplete"
              :loading="isLoading"
              loading-text="Loading..."
              :disabled="isLoading || !formControlEnabled || selectedGroupYears.length === 0"
              @update:model-value="
                (values) => handleGroupSelectionChange(values, $refs.selectGroupSubject, 'subject')
              "
            >
              <template #item="data">
                <v-list-item
                  v-bind="data.props"
                  :id="`student-selection--group-subject-item-${data.item.id}`"
                />
              </template>
            </v-autocomplete>
          </v-col>
        </div>
        <!-- CLASS -->
        <v-col cols="12" class="sub-label">
          <v-row align="center" justify="space-between">
            <div class="pl-1">
              <span>Class</span>
              <span :hidden="!filterFromSelectedSubject">&nbsp;(optional)</span>
            </div>
          </v-row>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" class="input-container px-0">
          <v-autocomplete
            id="student-selection--group-class"
            ref="selectGroupClass"
            v-model="selectedGroupClassDisplayed"
            placeholder="Select one or more classes"
            :items="[{ id: 'all', name: 'Select All' }, ...groupClassOptions]"
            item-title="name"
            item-value="id"
            multiple
            clearable
            chips
            closable-chips
            color="primary"
            hide-details
            :hide-selected="autocompleteHideSelected"
            variant="solo"
            density="comfortable"
            clear-icon="mdi-close"
            class="selection-autocomplete"
            :loading="isLoading"
            loading-text="Loading..."
            :disabled="isLoading || !formControlEnabled || selectedGroupYears.length === 0"
            @update:model-value="
              (values) => handleGroupSelectionChange(values, $refs.selectGroupClass, 'class')
            "
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :id="`student-selection--group-class-item-${item.id}`" />
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" class="sub-label">
          <v-row align="center">
            <div style="width: 213px">
              <v-checkbox
                id="cbx-filter-from-subject"
                v-model="filterFromSelectedSubject"
                label="Filter from Subject selection"
                color="primary"
                density="compact"
                hide-details="true"
                :disabled="isLoading || !formControlEnabled || selectedGroupYears.length === 0"
              ></v-checkbox>
            </div>
            <stb-icon-tooltip
              location="top"
              icon-color="rgb(119, 119, 119)"
              icon-id="fa:fas fa-info-circle"
              clickable
              max-width="450px"
            >
              <div class="type-tooltip-content">
                <span>Checking this box makes the 'Class' field optional. </span>
                <span>If a class is selected, the list of students from the chosen subject </span>
                <span>will be filtered to show only those in that class.</span>
              </div>
            </stb-icon-tooltip>
          </v-row>
        </v-col>
      </div>
      <div :hidden="!otherGroupEnabled">
        <!-- OTHER -->
        <v-col cols="12" class="sub-label">
          <v-row align="center" justify="space-between">
            <div>Co-curricular and Sport</div>
          </v-row>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" class="input-container px-0">
          <v-autocomplete
            id="student-selection--group-other"
            ref="selectGroupOther"
            v-model="selectedGroupOthersDisplayed"
            placeholder="Select one or more other groups"
            :items="[{ id: 'all', name: 'Select All' }, ...groupOtherOptions]"
            item-title="name"
            item-value="id"
            multiple
            clearable
            chips
            closable-chips
            color="primary"
            icon-color="primary"
            hide-details
            :hide-selected="autocompleteHideSelected"
            variant="solo"
            density="comfortable"
            clear-icon="mdi-close"
            class="selection-autocomplete"
            :loading="isLoading"
            loading-text="Loading..."
            :disabled="isLoading || !formControlEnabled"
            @update:model-value="
              (values) => handleGroupSelectionChange(values, $refs.selectGroupOther, 'other')
            "
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :id="`student-selection--group-other-item-${item.id}`" />
            </template>
          </v-autocomplete>
        </v-col>
      </div>
    </div>
    <div>
      <cs-form-dialog
        id="warning-cancel-student-selection-dialog"
        v-model="warningCancelStudentSelectionDialogDisplayed"
        heading="Student Selection"
        :primary-action="{
          label: 'Continue',
          eventName: 'primary-click'
        }"
        @primary-click="cancelStudentSelection"
      >
        <template #cs-form-dialog-content>
          <div>
            <span
              >Cancel will revert all selected groups and students to the last confirmed
              selection.</span
            >
            <br /><br />
            <span>Do you still want to proceed?</span>
          </div>
        </template>
      </cs-form-dialog>
    </div>
    <cs-form-dialog
      id="dlg-update-student-selection-warning"
      v-model="warningUpdateStudentSelectionDialogDisplayed"
      body-id="dlg-body-update-student-selection-warning"
      heading="Update Student Selection"
      :primary-action="{
        label: 'Confirm',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="confirmStudentSelection"
      @secondary1-click="warningUpdateStudentSelectionDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <div>
          <span>This action may populate a new checklist.</span>
          <br /><br />
          <span>Do you still want to proceed?</span>
        </div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import StbIconTooltip from '@/components/StbIconTooltip';
import buildStudentListQuery from '@/lib/buildStudentListQuery';

import moment from 'moment-timezone';
import { isEqual, toNumber } from 'lodash';

export default {
  components: { StbIconTooltip },
  props: {
    formControlEnabled: {
      type: Boolean,
      default: false
    },
    isExcursionInitiatedFromPcgNextGen: {
      type: Boolean,
      default: false
    }
  },

  emits: ['toggle', 'save'],

  data() {
    return {
      isLoading: true,
      clientData: null,
      groupData: null,
      studentData: [],
      studentSelectionContentDisplayed: true,
      studentSelectionDialogDisplayed: false,
      otherGroupEnabled: false,
      selectStudentEnabled: false,
      selectedGroupSchools: [],
      selectedGroupYears: [],
      selectedGroupClasses: [],
      selectedGroupSubjects: [],
      selectedGroupOthers: [],
      autocompleteHideSelected: false,
      studentOptions: [],
      selectedStudents: [],
      studentDataTimestamp: '',
      filterStudentText: '',
      filterStudentShowSelectedOnly: false,
      filterFromSelectedSubject: false,
      warningCancelStudentSelectionDialogDisplayed: false,
      warningUpdateStudentSelectionDialogDisplayed: false
    };
  },
  computed: {
    impersonatedClientSid() {
      return this.$store.state.common.impersonatedClientSid;
    },
    clientSid() {
      return this.$store.state.common.userClientSid;
    },
    excursionId() {
      return this.$store.state.common.excursionDc?.id;
    },
    apiParam() {
      return this.$store.state.common.apiParam;
    },
    excursionStudentManagementVendorSid() {
      return this.$store.state.common.excursionDc?.activeStudentManagementVendorSid;
    },
    showStudentSelection() {
      if (!this.studentSelectionInRiskAssessmentEnabled) {
        return false;
      }

      if (!this.isCreatedWithExcursionManagement) {
        return true;
      }

      if (this.isExcursionInitiatedFromPcgNextGen && this.isCreatedWithExcursionManagement) {
        return true;
      }

      return false;
    },
    isCreatedWithExcursionManagement() {
      return this.$store.state.common.excursionDc?.vendorData?.vendorExcursionSid?.length > 0;
    },
    groupSchoolOptions() {
      if (this.groupData?.schools) {
        return this.groupData.schools.map(({ id, name }) => ({ id: String(id), name }));
      }

      return [];
    },
    groupYearOptions() {
      if (this.groupData?.schools) {
        return this.groupData.schools.flatMap((school) => {
          if (this.selectedGroupSchools.indexOf(String(school.id)) === -1) {
            return [];
          }

          return (school.years || []).map((year) => ({ id: String(year.id), name: year.name }));
        });
      }

      return [];
    },
    groupClassOptions() {
      if (this.groupData?.schools) {
        return this.groupData.schools.flatMap((school) =>
          (school.years || []).flatMap((year) => {
            if (this.selectedGroupYears.indexOf(String(year.id)) === -1) {
              return [];
            }

            return (year.classes || []).map((studentClass) => ({
              id: String(studentClass.id),
              name: studentClass.name
            }));
          })
        );
      }

      return [];
    },
    groupSubjectOptions() {
      if (this.groupData?.schools) {
        return this.groupData.schools.flatMap((school) =>
          (school.years || []).flatMap((year) => {
            if (this.selectedGroupYears.indexOf(String(year.id)) === -1) {
              return [];
            }

            return (year.subjects || []).map((subject) => ({
              id: String(subject.id),
              name: subject.name
            }));
          })
        );
      }

      return [];
    },
    groupOtherOptions() {
      if (this.groupData?.cocurriculums) {
        return this.groupData.cocurriculums.map((cocurriculum) => ({
          id: String(cocurriculum.id),
          name: cocurriculum.name
        }));
      }

      return [];
    },
    groupNameById() {
      return [
        ...this.groupClassOptions,
        ...this.groupSubjectOptions,
        ...this.groupOtherOptions
      ].reduce((map, group) => ({ ...map, [String(group.id)]: group.name }), {});
    },
    filteredStudentOptions() {
      let retValues = [];

      if (this.filterStudentShowSelectedOnly) {
        retValues = this.selectedStudents;
      } else {
        retValues = this.studentOptions;
      }

      return retValues;
    },
    formatedStudentDataTimestamp() {
      let retValue = 'Never';

      if (this.studentDataTimestamp.length > 0) {
        retValue = moment(this.studentDataTimestamp)
          .utc()
          .tz(this.$store.state.common.clientTimezone)
          .format('MM/DD/YYYY, hh:mm:ss A');
      }

      return retValue;
    },
    disabledSelectStudentButton() {
      if (this.otherGroupEnabled) {
        return this.selectedGroupOthers.length === 0;
      }

      if (this.filterFromSelectedSubject) {
        return this.selectedGroupSubjects.length === 0;
      }

      return this.selectedGroupClasses.length === 0;
    },
    confirmedStudentInfo: {
      get() {
        const defaultValue = {
          studentLastUpdatedAt: '',
          studentDetailHash: {},
          selectionHash: {
            student: [],
            school: [],
            year: [],
            class: [],
            subject: [],
            custom: []
          }
        };

        return this.$store.state.common.excursionDc.studentInfo || defaultValue;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_EXCURSION_STUDENT_INFO, value);
      }
    },
    totalSelectedStudents() {
      return this.confirmedStudentInfo.selectionHash.student.length;
    },
    studentAttendanceCount: {
      get() {
        return this.$store.state.common.excursionDc.studentAttendanceCount;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_STUDENT_ATTENDANCE_COUNT, value);
      }
    },
    hasNoSelectedStudent() {
      return this.selectedStudents.length === 0;
    },
    selectedGroupSchoolDisplayed: {
      get() {
        return this.filterSelectedExistedInGroup(
          this.selectedGroupSchools,
          this.groupSchoolOptions
        );
      },
      set(value) {
        this.selectedGroupSchools = value;
      }
    },
    selectedGroupYearsDisplayed: {
      get() {
        return this.filterSelectedExistedInGroup(this.selectedGroupYears, this.groupYearOptions);
      },
      set(value) {
        this.selectedGroupYears = value;
      }
    },
    selectedGroupClassDisplayed: {
      get() {
        return this.filterSelectedExistedInGroup(this.selectedGroupClasses, this.groupClassOptions);
      },
      set(value) {
        this.selectedGroupClasses = value;
      }
    },
    selectedGroupSubjectDisplayed: {
      get() {
        return this.filterSelectedExistedInGroup(
          this.selectedGroupSubjects,
          this.groupSubjectOptions
        );
      },
      set(value) {
        this.selectedGroupSubjects = value;
      }
    },
    selectedGroupOthersDisplayed: {
      get() {
        return this.filterSelectedExistedInGroup(this.selectedGroupOthers, this.groupOtherOptions);
      },
      set(value) {
        this.selectedGroupOthers = value;
      }
    },
    isStudentCountSynced() {
      return (
        this.totalSelectedStudents === 0 ||
        toNumber(this.studentAttendanceCount) === this.totalSelectedStudents
      );
    },
    isVendorServiceEnded() {
      return this.$store.state.excursions.excursionConfig?.student?.vendor?.isServiceEnded || false;
    },
    studentSelectionInRiskAssessmentEnabled() {
      return (
        this.$store.state.excursions.excursionConfig?.student?.isStudentSelectionEnabled || false
      );
    }
  },
  watch: {
    async filterFromSelectedSubject(newValue) {
      if (!newValue) {
        this.autocompleteHideSelected = false;
        await this.$nextTick();

        this.selectedGroupSubjects = [];

        await this.$nextTick();
        this.autocompleteHideSelected = true;
      }
    }
  },
  async mounted() {
    await this.getData();
    this.isLoading = false;
  },
  methods: {
    async getData() {
      let setupResult;
      if (this.impersonatedClientSid) {
        setupResult = await this.getStudentManagementSetup();
        if (setupResult) {
          this.resetSelection();
          await this.$nextTick();
          this.autocompleteHideSelected = true;
        }
      } else {
        await this.getDataAsNonSysAdmin();
      }
    },
    async getStudentManagementSetup(includeSubdomain = false) {
      const { 'vendor-sid': vendorSid, 'oauth-signature': oauthSignature } = this.apiParam;
      try {
        const client = await this.$store.dispatch('clients/fetchClientBySid', {
          sid: this.clientSid,
          vendorSid,
          oauthSignature
        });
        this.clientData = {
          schoolType: client.metadata.schoolType,
          subdomain: includeSubdomain ? client.metadata.subdomain : ''
        };
        if (this.studentSelectionInRiskAssessmentEnabled) {
          const groupData = await this.fetchClientStudentGroups();
          if (groupData) {
            this.groupData = groupData;
            return true;
          }
        }
        return false;
      } catch (e) {
        console.error('CLIENT_STUDENT_GROUPS', 'Failed to retrieve client student groups');
        return false;
      }
    },
    async getDataAsNonSysAdmin() {
      const queryParam = {
        ...this.apiParam,
        'excursion-id': this.excursionId
      };
      const cbGetExcursionClientSetup = await this.$app.stbApiAdapter.getClientSetups({
        queryParam
      });

      if (!cbGetExcursionClientSetup.error && cbGetExcursionClientSetup.data) {
        this.clientData = {
          schoolType: cbGetExcursionClientSetup.data.computed?.clientSchoolType,
          subdomain: cbGetExcursionClientSetup.data.computed?.clientSubdomain
        };
        if (!this.studentSelectionInRiskAssessmentEnabled) return;
        const groupData = await this.fetchClientStudentGroups();
        if (groupData) {
          this.groupData = groupData;
          this.resetSelection();

          await this.$nextTick();
          this.autocompleteHideSelected = true;
        }
      } else {
        console.error('CLIENT_SETUP', 'Failed to retrieve client excursion setup');
      }
    },
    resetSelection() {
      this.selectedGroupSchools = this.confirmedStudentInfo.selectionHash.school || [];
      this.selectedGroupYears = this.confirmedStudentInfo.selectionHash.year || [];
      this.selectedGroupClasses = this.confirmedStudentInfo.selectionHash.class || [];
      this.selectedGroupSubjects = this.confirmedStudentInfo.selectionHash.subject || [];
      this.selectedGroupOthers = this.confirmedStudentInfo.selectionHash.custom || [];
      this.filterFromSelectedSubject = this.confirmedStudentInfo.selectionHash.subject?.length > 0;
      this.otherGroupEnabled = this.confirmedStudentInfo.selectionHash.custom?.length > 0;
    },
    async fetchClientStudentGroups() {
      try {
        const result = await this.$store.dispatch('clients/fetchClientStudentGroups', {
          clientSid: this.clientSid,
          queryParam: this.apiParam
        });

        return result;
      } catch (e) {
        console.error('CLIENT_STUDENT_GROUPS', 'Failed to retrieve client student groups');
        return null;
      }
    },
    toggle() {
      this.studentSelectionContentDisplayed = !this.studentSelectionContentDisplayed;
      this.$emit('toggle', this.studentSelectionContentDisplayed);
    },
    async handleGroupSelectionChange(values, ref, type) {
      ref.blur();
      this.autocompleteHideSelected = false;

      await this.$nextTick();

      switch (type) {
        case 'school':
          if (values.includes('all')) {
            this.selectedGroupSchools = this.groupSchoolOptions.map((item) => item.id);
          }
          this.selectedGroupYears = [];
          this.selectedGroupClasses = [];
          this.selectedGroupSubjects = [];
          this.selectedGroupOthers = [];
          break;

        case 'year':
          if (values.includes('all')) {
            this.selectedGroupYears = this.groupYearOptions.map((item) => item.id);
          }
          this.selectedGroupClasses = [];
          this.selectedGroupSubjects = [];
          break;

        case 'class':
          if (values.includes('all')) {
            this.selectedGroupClasses = this.groupClassOptions.map((item) => item.id);
          }
          break;

        case 'subject':
          if (values.includes('all')) {
            this.selectedGroupSubjects = this.groupSubjectOptions.map((item) => item.id);
          }
          break;

        case 'other':
          if (values.includes('all')) {
            this.selectedGroupOthers = this.groupOtherOptions.map((item) => item.id);
          }
          this.selectedGroupSchools = [];
          this.selectedGroupYears = [];
          this.selectedGroupClasses = [];
          this.selectedGroupSubjects = [];
          this.filterFromSelectedSubject = false;
          break;

        default:
          this.selectedGroupSchools = [];
          this.selectedGroupYears = [];
          this.selectedGroupClasses = [];
          this.selectedGroupSubjects = [];
          this.selectedGroupOthers = [];
          this.filterFromSelectedSubject = false;
      }

      await this.$nextTick();

      ref.focus();
      this.autocompleteHideSelected = true;
    },
    async openStudentSelection() {
      this.filterStudentText = '';
      this.filterStudentShowSelectedOnly = false;

      this.studentOptions = Object.entries(this.confirmedStudentInfo.studentDetailHash).map(
        ([id, item]) => {
          const { name } = item;
          const group = item.groups
            .map((group) => this.groupNameById[String(group)] || group)
            .join(', ');

          return { id, name, group };
        }
      );
      this.setSelectedStudents();

      this.studentData = Object.entries(this.confirmedStudentInfo.studentDetailHash).map(
        ([id, item]) => {
          const { name } = item;
          const { groups } = item;

          return { id, name, groups };
        }
      );

      this.studentDataTimestamp = this.confirmedStudentInfo.studentLastUpdatedAt;
      this.studentSelectionDialogDisplayed = true;

      await this.$nextTick();
      if (this.isVendorServiceEnded) {
        await this.showStudentSelectionDialogAlert('warning');
        await this.$nextTick();
        return;
      }

      const {
        school: prevSelectedSchools = [],
        year: prevSelectedYears = [],
        class: prevSelectedClasses = [],
        subject: prevSelectedSubjects = [],
        custom: prevSelectedCustoms = []
      } = this.confirmedStudentInfo.selectionHash;
      const isGroupSchoolChanged = !isEqual(
        [...this.selectedGroupSchools].sort(),
        [...prevSelectedSchools].sort()
      );
      const isGroupYearChanged = !isEqual(
        [...this.selectedGroupYears].sort(),
        [...prevSelectedYears].sort()
      );
      const isGroupClassChanged = !isEqual(
        [...this.selectedGroupClasses].sort(),
        [...prevSelectedClasses].sort()
      );
      const isGroupSubjectChanged = !isEqual(
        [...this.selectedGroupSubjects].sort(),
        [...prevSelectedSubjects].sort()
      );
      const isGroupOtherChanged = !isEqual(
        [...this.selectedGroupOthers].sort(),
        [...prevSelectedCustoms].sort()
      );

      if (
        this.studentDataTimestamp.length === 0 ||
        isGroupSchoolChanged ||
        isGroupYearChanged ||
        isGroupClassChanged ||
        isGroupSubjectChanged ||
        isGroupOtherChanged
      ) {
        await this.refreshStudentSelection();
      }
    },
    async refreshStudentSelection(readFromCache = true) {
      this.isLoading = true;

      await this.$nextTick();

      const studentListFilters = buildStudentListQuery({
        otherGroupEnabled: this.otherGroupEnabled,
        filterFromSelectedSubject: this.filterFromSelectedSubject,
        selectedGroupSubjects: this.selectedGroupSubjects,
        selectedGroupClasses: this.selectedGroupClasses,
        selectedGroupOthers: this.selectedGroupOthers
      });

      try {
        const { students } = await this.$store.dispatch('clients/fetchClientStudentList', {
          clientSid: this.clientSid,
          ...studentListFilters,
          queryParam: this.apiParam,
          readFromCache
        });

        this.studentData = students.map((student) => ({
          id: student.id,
          name: student.name,
          groups: student.groupNames
        }));
        this.studentOptions = students.map((student) => ({
          id: student.id,
          name: student.name,
          group: student.groupNames.join(', ')
        }));
        this.studentDataTimestamp = new Date().toISOString();
        this.setSelectedStudents(true);
      } catch (e) {
        console.error('CLIENT_STUDENT', 'Failed to retrieve student list');
      }

      this.isLoading = false;
    },
    async confirmStudentSelection() {
      this.studentSelectionDialogDisplayed = false;
      this.warningUpdateStudentSelectionDialogDisplayed = false;

      const newConfirmedStudentInfo = {
        studentLastUpdatedAt: this.studentDataTimestamp,
        studentDetailHash: this.studentData.reduce((map, item) => {
          return { ...map, [item.id]: item };
        }, {}),
        selectionHash: {
          student: this.selectedStudents.map((item) => item.id),
          school: this.selectedGroupSchoolDisplayed,
          year: this.selectedGroupYearsDisplayed,
          class: this.selectedGroupClassDisplayed,
          subject: this.selectedGroupSubjectDisplayed,
          custom: this.selectedGroupOthers
        }
      };

      this.studentDataTimestamp = newConfirmedStudentInfo.studentLastUpdatedAt;
      this.confirmedStudentInfo = newConfirmedStudentInfo;
      if (!this.isExcursionInitiatedFromPcgNextGen) {
        this.studentAttendanceCount = newConfirmedStudentInfo.selectionHash.student.length || '';
      }

      const {
        school: selectedSchools,
        year: selectedYears,
        class: selectedClasses,
        subject: selectedSubjects,
        custom: selectedCocurriculums
      } = this.confirmedStudentInfo.selectionHash;

      const allStudents = this.studentOptions.map((student) => ({
        id: student.id,
        isSelected: this.selectedStudents.some((selected) => selected.id === student.id)
      }));

      this.$emit('save', {
        selectedGroups: this.buildStudentGroupData(
          selectedSchools,
          selectedYears,
          selectedClasses,
          selectedSubjects,
          selectedCocurriculums
        ),
        studentList: allStudents
      });
    },

    buildStudentGroupData(
      selectedSchools,
      selectedYears,
      selectedClasses,
      selectedSubjects,
      selectedCocurriculums
    ) {
      const schoolIds = new Set(selectedSchools.map((id) => String(id)));
      const yearIds = new Set(selectedYears.map((id) => String(id)));
      const classIds = new Set(selectedClasses.map((id) => String(id)));
      const subjectIds = new Set(selectedSubjects.map((id) => String(id)));
      const cocurriculumIds = new Set(selectedCocurriculums.map((id) => String(id)));

      const schools = (this.groupData?.schools || [])
        .filter((school) => schoolIds.has(String(school.id)))
        .map((school) => ({
          id: String(school.id),
          name: school.name,
          years: (school.years || [])
            .filter((year) => yearIds.has(String(year.id)))
            .map((year) => ({
              id: String(year.id),
              name: year.name,
              classes: (year.classes || []).filter((cls) => classIds.has(String(cls.id))),
              subjects: (year.subjects || []).filter((subject) =>
                subjectIds.has(String(subject.id))
              )
            }))
        }));

      const cocurriculums = (this.groupData?.cocurriculums || []).filter((cocurriculum) =>
        cocurriculumIds.has(String(cocurriculum.id))
      );

      return {
        schools,
        cocurriculums
      };
    },

    async onCancelStudentSelection() {
      if (!this.formControlEnabled) {
        this.studentSelectionDialogDisplayed = false;
        return;
      }
      this.warningCancelStudentSelectionDialogDisplayed = true;
    },
    async cancelStudentSelection() {
      this.autocompleteHideSelected = false;
      await this.$nextTick();

      this.warningCancelStudentSelectionDialogDisplayed = false;
      this.studentSelectionDialogDisplayed = false;
      this.setSelectedStudents();

      this.selectedGroupSchools = this.confirmedStudentInfo.selectionHash.school || [];
      this.selectedGroupYears = this.confirmedStudentInfo.selectionHash.year || [];
      this.selectedGroupClasses = this.confirmedStudentInfo.selectionHash.class || [];
      this.selectedGroupSubjects = this.confirmedStudentInfo.selectionHash.subject || [];
      this.selectedGroupOthers = this.confirmedStudentInfo.selectionHash.custom || [];
      this.filterFromSelectedSubject = this.confirmedStudentInfo.selectionHash.subject?.length > 0;
      this.otherGroupEnabled = this.confirmedStudentInfo.selectionHash.custom?.length > 0;
      this.studentDataTimestamp = this.confirmedStudentInfo.studentLastUpdatedAt;

      await this.$nextTick();
      this.autocompleteHideSelected = true;
    },
    setSelectedStudents(keepSelection) {
      let selectedIds = [];
      if (keepSelection) {
        selectedIds = this.selectedStudents.map((item) => String(item.id));
      } else {
        selectedIds = this.confirmedStudentInfo.selectionHash.student.map((id) => String(id));
      }
      this.selectedStudents = this.studentOptions.filter((item) =>
        selectedIds.includes(String(item.id))
      );
    },
    filterSelectedExistedInGroup(selectedIds, referenceGroup) {
      if (!referenceGroup || !selectedIds) {
        return selectedIds;
      }
      const validIds = referenceGroup.map((item) => item.id);
      return selectedIds.filter((id) => validIds.includes(id));
    },
    async showStudentSelectionDialogAlert(type) {
      const vendorName =
        this.$store.state.excursions.excursionConfig?.student?.vendor?.name || 'vendor';
      const message = `Unable to load students from ${vendorName}. This service has ended and is no longer available. To continue using Safe Trip Builder, please contact your school administrator to update your integration settings and reach out to our support team for assistance with alternative options.`;
      this.$store.commit('cs-appearance/setAlert', {
        type,
        message,
        onDialogOnly: true
      });
      await this.$nextTick();
    }
  }
};
</script>

<style scoped>
.student-selection .sub-label {
  color: rgb(0, 0, 0, 0.6);
  padding-bottom: 7px;
}

.student-selection .input-container {
  padding-bottom: 10px;
}

.co-curricular-and-sport-groups-tooltip-button,
.subject-groups-tooltip-button {
  margin: 14px;
  font-size: 16px;
}

.student-selection :deep(.v-label.v-label--clickable) {
  font-size: 14px;
  font-family: Roboto, sans-serif;
}

.student-selection-checkbox {
  font-size: 16px;
}

.filter-student-text-field :deep(.v-field__clearable .mdi-close) {
  color: rgb(var(--v-theme-primary)) !important;
}

.selection-autocomplete :deep(.v-field__clearable .mdi-close) {
  color: rgb(var(--v-theme-primary));
}
</style>

<style>
#dlg-body-student-selection .cs-form-dialog-title .v-btn {
  display: none;
}
</style>
